import { useEffect, useRef, useState } from "react";
import { KeyRound, ShieldAlert, TriangleAlert } from "lucide-react";
import { useAdmin } from "../lib/useAdmin";
import { useFocusTrap } from "../lib/shortcuts";
import Kbd from "./Kbd";

/* Confirmation avant action destructive. Trois niveaux :
   - "simple"  : un bouton, mais les conséquences sont toujours listées ;
   - "type"    : il faut saisir le nom exact de la ressource ;
   - "reauth"  : un motif obligatoire, puis un jeton de présence à durée courte.

   Perform n'a pas de mot de passe : POST /api/admin/reauth délivre un jeton
   valable 5 minutes, renvoyé ensuite dans l'en-tête X-Admin-Reauth. C'est une
   preuve de présence dans la session courante, PAS un second facteur — aucun
   MFA n'existe sur la plateforme, et le dialogue ne prétend pas le contraire.
   Le motif est joint à l'action et se retrouve dans le journal d'audit.
   Le dialogue est modal, le focus y est piégé, Échap annule. */
const MIN_REASON = 10;

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  consequences = [],
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  tone = "danger",
  level = "simple",
  confirmPhrase,
  phraseHint,
  reasonLabel = "Motif de l'action",
  reasonHint = "Enregistré dans le journal d'audit avec votre identité. 10 caractères minimum.",
  busyLabel = "En cours…",
  testid = "confirm-dialog",
}) {
  const { reauth, admin } = useAdmin();
  const [phrase, setPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const panel = useRef(null);
  useFocusTrap(panel, open);

  useEffect(() => {
    if (!open) return;
    setPhrase("");
    setReason("");
    setError(null);
    setBusy(false);
  }, [open]);

  if (!open) return null;

  const expected = String(confirmPhrase ?? "").trim();
  const phraseOk = level !== "type" || (expected.length > 0 && phrase.trim() === expected);
  const reasonOk = level !== "reauth" || reason.trim().length >= MIN_REASON;
  const ready = phraseOk && reasonOk && !busy;
  const Icon = level === "reauth" ? KeyRound : tone === "danger" ? TriangleAlert : ShieldAlert;
  const mainLabel = confirmLabel;

  const cancel = () => { if (!busy) onClose?.(); };

  const submit = async event => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      let granted = null;
      if (level === "reauth") {
        // Le jeton est délivré immédiatement et posé dans X-Admin-Reauth
        // par adminApi : l'appel suivant passera la vérification serveur.
        granted = await reauth({ reason: reason.trim() });
      }
      await onConfirm?.(granted, reason.trim());
      onClose?.();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <div
      className="adm-dialog-backdrop"
      onMouseDown={e => { if (e.target === e.currentTarget) cancel(); }}
      onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); cancel(); } }}
    >
      <form
        ref={panel}
        className={`adm-dialog${tone === "danger" ? " is-danger" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testid}-title`}
        aria-describedby={description ? `${testid}-desc` : undefined}
        onSubmit={submit}
        data-testid={testid}
      >
        <div className="adm-dialog-head">
          <span className="adm-dialog-icon" aria-hidden="true"><Icon size={16} /></span>
          <div>
            <h2 id={`${testid}-title`}>{title}</h2>
            {description && <p id={`${testid}-desc`}>{description}</p>}
          </div>
        </div>

        <div className="adm-dialog-body">
          {consequences.length > 0 && (
            <ul className="adm-consequences" data-testid={`${testid}-consequences`}>
              {consequences.map((item, i) => (
                <li key={i}><TriangleAlert size={13} aria-hidden="true" />{item}</li>
              ))}
            </ul>
          )}

          {level === "type" && (
            <>
              <label className="adm-label" htmlFor={`${testid}-phrase`}>
                Pour confirmer, saisissez <span className="adm-confirm-phrase">{expected}</span>
              </label>
              <input
                id={`${testid}-phrase`}
                className="adm-input"
                value={phrase}
                onChange={e => setPhrase(e.target.value)}
                autoComplete="off"
                spellCheck="false"
                data-autofocus
                data-testid={`${testid}-phrase`}
              />
              {phraseHint && <p className="adm-hint">{phraseHint}</p>}
            </>
          )}

          {level === "reauth" && (
            <>
              <label className="adm-label" htmlFor={`${testid}-reason`}>{reasonLabel}</label>
              <textarea
                id={`${testid}-reason`}
                className="adm-textarea"
                value={reason}
                onChange={e => setReason(e.target.value)}
                data-autofocus
                data-testid={`${testid}-reason`}
              />
              <p className="adm-hint">
                {reasonHint}{" "}
                {reason.trim().length < MIN_REASON && <span>({reason.trim().length}/{MIN_REASON})</span>}
              </p>
            </>
          )}

          {error && (
            <div className="adm-dialog-error" role="alert" data-testid={`${testid}-error`} style={{ marginTop: 12 }}>
              <TriangleAlert size={14} />
              <span>{error.message || "L'action a échoué."}</span>
            </div>
          )}
        </div>

        <div className="adm-dialog-foot">
          <Kbd combo="esc" />
          <button type="button" className="adm-btn" onClick={cancel} disabled={busy} data-testid={`${testid}-cancel`}>
            {cancelLabel}
          </button>
          <button
            type="submit"
            className={`adm-btn ${tone === "danger" && !sendingStep ? "is-danger" : "is-primary"}`}
            disabled={!ready}
            data-testid={`${testid}-confirm`}
          >
            {busy ? busyLabel : mainLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
